/**
 * Status normalization constants
 */
export const SUCCESS_STATES = new Set([
  'SUCCESS',
  'COMPLETED',
  'COMPLETE',
  'DONE',
  'SUCCEEDED',
]);

export const FAILURE_STATES = new Set([
  'FAIL',
  'FAILED',
  'FAILURE',
  'ERROR',
]);

export const IN_PROGRESS_STATES = new Set([
  'PENDING',
  'IN_PROGRESS',
  'IN PROGRESS',
  'PROCESSING',
  'RUNNING',
  'QUEUED',
]);

/**
 * Normalizes status string to uppercase and trims whitespace
 * @param {string|Object} status - Status string or object containing status
 * @returns {string} Normalized status string
 */
export function normalizeStatus(status) {
  if (!status) return '';

  if (typeof status === 'string') {
    return status.trim().toUpperCase();
  }

  if (typeof status === 'object') {
    const candidates = [
      status.status,
      status.state,
      status.jobStatus,
      status.jobState,
      status.data?.status,
      status.data?.state,
      status.result?.status,
      status.result?.state,
    ];

    for (const candidate of candidates) {
      if (isPresentStatus(candidate)) {
        return String(candidate).trim().toUpperCase();
      }
    }
  }

  return '';
}

/**
 * Checks if a value is a present (non-empty) status
 * @param {*} value - Value to check
 * @returns {boolean} True if the value is a present status
 */
function isPresentStatus(value) {
  if (value == null) return false;
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return true;
}

/**
 * Checks if status represents a successful state
 * @param {string|Object} status - Status to check
 * @returns {boolean} True if status is successful
 */
export function isSuccessStatus(status) {
  const normalized = normalizeStatus(status);
  return SUCCESS_STATES.has(normalized);
}

/**
 * Checks if status represents a failed state
 * @param {string|Object} status - Status to check
 * @returns {boolean} True if status is failed
 */
export function isFailureStatus(status) {
  const normalized = normalizeStatus(status);
  return FAILURE_STATES.has(normalized);
}

/**
 * Checks if status represents an in-progress state
 * @param {string|Object} status - Status to check
 * @returns {boolean} True if status is in progress
 */
export function isInProgressStatus(status) {
  const normalized = normalizeStatus(status);
  return IN_PROGRESS_STATES.has(normalized);
}

/**
 * Gets a user-friendly status label
 * @param {string|Object} status - Status to get label for
 * @returns {string} User-friendly status label
 */
export function getStatusLabel(status) {
  const normalized = normalizeStatus(status);

  if (isSuccessStatus(status)) return 'Complete';
  if (normalized === 'PENDING') return 'Queued — waiting to start...';
  if (isInProgressStatus(status)) return 'Generating animation...';

  return normalized || '';
}